import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt, faSearch, faSun, faMoon, faBars, faCog, faChevronDown, faWandMagicSparkles, faInbox } from '@fortawesome/free-solid-svg-icons';
import AppSwitcher from './AppSwitcher';
import QuickCreate from './QuickCreate';

const ROTULOS = {
  inicio: 'Início',
  clientes: 'Clientes',
  projetos: 'Projetos',
  financeiro: 'Financeiro',
  estoque: 'Estoque',
  patrimonio: 'Patrimônio',
  documentos: 'Documentos',
};

function iniciais(nome) {
  if (!nome) return '?';
  const partes = nome.trim().split(/\s+/);
  return ((partes[0]?.[0] || '') + (partes[1]?.[0] || '')).toUpperCase() || partes[0][0].toUpperCase();
}

// UserMenu — antes era só um ícone solto de "sair" na topbar, sem nenhuma
// presença de identidade (nome/empresa apareciam só dentro do Configurações,
// atrás de um clique). Reconstruído como um cluster único (avatar + nome)
// com dropdown, padrão comum em SaaS (Linear/Attio/Notion).
function UserMenu({ nomeUsuario, empresaNome, onAbrirConfig, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('click', onClickOutside);
    return () => document.removeEventListener('click', onClickOutside);
  }, []);
  return (
    <div className="crm-dropdown" ref={ref}>
      <button className="crm-user-menu-trigger" onClick={() => setOpen((v) => !v)}>
        <span className="crm-avatar crm-avatar-sm">{iniciais(nomeUsuario)}</span>
        <span className="crm-user-menu-name">{nomeUsuario || 'Conta'}</span>
        <FontAwesomeIcon icon={faChevronDown} className="crm-user-menu-chevron" />
      </button>
      {open && (
        <div className="crm-dropdown-menu">
          <div className="crm-dropdown-section-label">{empresaNome || 'Sua conta'}</div>
          <button className="crm-dropdown-item" onClick={() => { setOpen(false); onAbrirConfig(); }}>
            <FontAwesomeIcon icon={faCog} /> Configurações
          </button>
          <button className="crm-dropdown-item crm-dropdown-item-danger" onClick={() => { setOpen(false); onLogout(); }}>
            <FontAwesomeIcon icon={faSignOutAlt} /> Sair
          </button>
        </div>
      )}
    </div>
  );
}

// Topbar reconstruída (fase 3) — a versão anterior era uma fileira de
// ícones soltos sem hierarquia visual nenhuma (busca/tema/criar/switcher/
// sair, todos do mesmo tamanho e peso). Agora a busca global tem presença
// real (input-like, não só uma lupa) e usuário/empresa aparecem como
// identidade (UserMenu), não como um ícone anônimo de saída.
function Topbar({ selecionado, onSelecionar, modulosHabilitados, permissaoClientes, crmAcessivel, onLogout, onAbrirBusca, tema, onAlternarTema, onAbrirMenuMobile, nomeUsuario, empresaNome, onAbrirConfig, onAbrirAgente, onAbrirSugestoes, sugestoesPendentes }) {
  return (
    <header className="crm-topbar">
      <button className="crm-icon-btn crm-topbar-menu-btn" title="Abrir menu" aria-label="Abrir menu" onClick={onAbrirMenuMobile}>
        <FontAwesomeIcon icon={faBars} />
      </button>
      <div className="crm-breadcrumb">
        <span className="crm-breadcrumb-current">{ROTULOS[selecionado] || 'CRM'}</span>
      </div>

      <button className="crm-topbar-search" onClick={onAbrirBusca}>
        <FontAwesomeIcon icon={faSearch} />
        <span>Buscar clientes, projetos, ativos, documentos...</span>
        <kbd className="crm-kbd">⌘K</kbd>
      </button>

      <div className="crm-topbar-spacer" />
      <div className="crm-topbar-actions">
        {crmAcessivel && (
          <button className="crm-btn crm-btn-outline crm-btn-sm crm-agent-trigger-btn" onClick={onAbrirAgente} title="Abrir o Assistente de IA">
            <FontAwesomeIcon icon={faWandMagicSparkles} /> Assistente
          </button>
        )}
        {crmAcessivel && (
          <button
            className="crm-icon-btn crm-sug-trigger"
            onClick={onAbrirSugestoes}
            title={sugestoesPendentes > 0 ? `${sugestoesPendentes} sugestão(ões) esperando decisão` : 'Sugestões da automação'}
            aria-label="Sugestões da automação"
          >
            <FontAwesomeIcon icon={faInbox} />
            {sugestoesPendentes > 0 && <span className="crm-sug-badge">{sugestoesPendentes > 99 ? '99+' : sugestoesPendentes}</span>}
          </button>
        )}
        <QuickCreate
          onSelecionar={onSelecionar}
          modulosHabilitados={modulosHabilitados}
          permissaoClientes={permissaoClientes}
          crmAcessivel={crmAcessivel}
        />
        <button className="crm-icon-btn" title={tema === 'dark' ? 'Tema claro' : 'Tema escuro'} aria-label="Alternar tema" onClick={onAlternarTema}>
          <FontAwesomeIcon icon={tema === 'dark' ? faSun : faMoon} />
        </button>
        <AppSwitcher />
        <UserMenu nomeUsuario={nomeUsuario} empresaNome={empresaNome} onAbrirConfig={onAbrirConfig} onLogout={onLogout} />
      </div>
    </header>
  );
}

export default Topbar;
